export type Json =
  | string
  | number
  | boolean
  | null
  | { [key: string]: Json | undefined }
  | Json[]

export type AccessRequest = {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  companyName?: string;
  licenseNumber?: string;
  message?: string;
  status: string; // 'pending', 'approved', or 'rejected'
  createdAt?: string;
  // For database compatibility
  first_name?: string;
  last_name?: string;
  company_name?: string;
  license_number?: string;
  created_at?: string;
};

export type UserProfile = {
  id: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  companyName?: string;
  licenseNumber?: string;
  licenseExpiry?: string;
  licenseType?: string;
  message?: string;
  isApproved?: boolean;
  role?: string;
  createdAt?: string;
  // New fields
  abnAcn?: string;
  companyAddress?: string;
  stateTerritory?: string;
  licenseDocumentUrl?: string;
  companyLogoUrl?: string;
  websiteUrl?: string;
  companyDescription?: string;
  operationalStatus?: string;
  registrationDate?: string;
  termsAgreement?: boolean;
  isBanned?: boolean;
  // For database compatibility
  first_name?: string;
  last_name?: string;
  company_name?: string;
  license_number?: string;
  license_expiry?: string;
  license_type?: string;
  is_approved?: boolean;
  created_at?: string;
  abn_acn?: string;
  company_address?: string;
  state_territory?: string;
  license_document_url?: string;
  company_logo_url?: string;
  website_url?: string;
  company_description?: string;
  operational_status?: string;
  registration_date?: string;
  terms_agreement?: boolean;
  is_banned?: boolean;
};

export type CostItem = {
  unit: string;
  price: number;
  description?: string;
};

export type Cultivar = {
  id: string;
  cultivar: string;
  format: string;
  description?: string;
  images?: string[];
  parents?: string[];
  rating_citrus?: number;
  rating_earthy?: number;
  rating_fruity?: number;
  rating_dank?: number;
  rating_dessert?: number;
  rating_pungent?: number;
  feeling?: string;
  aroma?: string;
  advertising_start_date?: string;
  advertising_end_date?: string;
  created_at?: string;
  updated_at?: string;
  indica_percentage?: number;
  
  // Fields for TypeScript compatibility
  lineage?: string;
  country?: string;
  vendor?: string;
  visible_in_marketplace?: boolean;
  
  // Keep these fields for backward compatibility
  // (even though they've been moved to batches)
  price_per_kg?: number;
  grow_style?: string;
  grow_media?: string;
  grow_method?: string;
  feed?: string;
  available_date?: string; 
  batch_number?: string;
  quantity_kg?: number;
  moq?: number;
  harvest_date?: string;
  processed_date?: string;
  lighting?: string;
  harvest_method?: string;
  separation_method?: string;
  extraction_method?: string;
  compliance_heavy_metals?: string;
  compliance_mycotoxins?: string;
  compliance_microbiology?: string;
  compliance_pesticides?: string;
  
  // Backward compatibility fields
  availableDate?: string;
  createdAt?: string;
  updatedAt?: string;
  thcPercentage?: number;
  cbdPercentage?: number;
  growStyle?: string;
  growMedia?: string;
  terpene_info?: {
    major_terpenes: TerpeneWithPercentage[];
    total_terpenes_percentage: number;
  };
};

// Define Product type alias for Cultivar to maintain compatibility with existing code
export type Product = Cultivar;

export const mapSupabaseProduct = (item: any): Product => {
  return {
    id: item.id,
    cultivar: item.cultivar || "",
    format: item.format || "",
    description: item.description || "",
    images: item.images || [],
    parents: item.parents || [],
    rating_citrus: item.rating_citrus || 0,
    rating_earthy: item.rating_earthy || 0,
    rating_fruity: item.rating_fruity || 0,
    rating_dank: item.rating_dank || 0,
    rating_dessert: item.rating_dessert || 0,
    rating_pungent: item.rating_pungent || 0,
    feeling: item.feeling || "",
    aroma: item.aroma || "",
    advertising_start_date: item.advertising_start_date,
    advertising_end_date: item.advertising_end_date,
    created_at: item.created_at,
    updated_at: item.updated_at,
    indica_percentage: item.indica_percentage || 50,
    
    // Add missing fields
    lineage: item.lineage || "",
    country: item.country || "",
    vendor: item.vendor || "",
    visible_in_marketplace: item.visible_in_marketplace ?? false,
    
    // Keep compatibility fields
    price_per_kg: item.price_per_kg,
    grow_style: item.grow_style,
    grow_media: item.grow_media,
    grow_method: item.grow_method,
    feed: item.feed, 
    available_date: item.available_date,
    batch_number: item.batch_number,
    quantity_kg: item.quantity_kg,
    moq: item.moq,
    
    // Add backward compatibility fields
    availableDate: item.available_date,
    createdAt: item.created_at,
    updatedAt: item.updated_at,
    thcPercentage: 0,
    cbdPercentage: 0,
    growStyle: item.grow_style,
    growMedia: item.grow_media
  };
};

// Update ProductOption type to be more specific
export type ProductFormat = string;
export type GrowMethod = string;
export type GrowMedia = string;
export type FeedType = string;
export type GrowStyle = string;
export type ProductOption = { label: string; value: string };
export type Terpene = string;
export type TerpeneWithPercentage = { name: string; percentage: number };
export type TerpeneInfo = { name: string; description: string; effects: string[] };
export type SupabaseProduct = any;

// Updated Hardware type to match the database schema
export interface Hardware {
  id: string;
  name?: string;
  manufacture: string;
  product: string;
  part_number: string;
  capacity?: string;
  product_type: string;
  colour?: string;
  description: string;
  specification: string;
  images: string[];
  currency_conversion_rate: number;
  exchange_rate_buffer_percent: number;
  shipping_cost: number;
  gst_rate: number;
  markup_rate: number;
  markup_per_unit: number;
  use_fixed_margin: boolean;
  gst_on_shipping: boolean;
  rounding: number;
  created_at: string | null;
  updated_at: string | null;
  visible_in_marketplace: boolean;
  bulk_cost?: number;
  moq?: number;
}
